import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { AgentDetail } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import type { AgentDraft } from "@/features/agents/components/AgentDraft";
import { PrePublishSummary } from "@/features/agents/components/PrePublishSummary";
import {
  useAgentPublishRequests,
  useRequestPublish,
  useUnpublishAgent,
} from "@/features/agents/hooks/useAgentPublish";

interface Props {
  draft: AgentDraft;
  onChange: (next: AgentDraft) => void;
  /** Status real do server -- usado para mostrar badge + decidir botoes. */
  agent?: AgentDetail;
  agentId?: string;
}

export function PublicationTab({ draft, onChange, agent, agentId }: Props) {
  const { t } = useTranslation("agents");
  const requestPublish = useRequestPublish(agentId ?? "");
  const unpublish = useUnpublishAgent(agentId ?? "");
  const requests = useAgentPublishRequests(agentId ?? "");
  const status = agent?.status;

  function statusBadge(s: string | undefined) {
    if (s === "published")
      return (
        <Badge className="bg-green-600 text-white">
          {t("publication.statusBadges.published")}
        </Badge>
      );
    if (s === "pending_approval")
      return (
        <Badge className="bg-yellow-500 text-white">
          {t("publication.statusBadges.pending_approval")}
        </Badge>
      );
    if (s === "archived")
      return (
        <Badge variant="secondary">
          {t("publication.statusBadges.archived")}
        </Badge>
      );
    return (
      <Badge variant="outline">{t("publication.statusBadges.draft")}</Badge>
    );
  }

  async function handleRequestPublish() {
    if (!agentId) return;
    try {
      await requestPublish.mutateAsync({});
      toast.success(t("publication.toasts.requested"));
    } catch (err) {
      toastApiError(err);
    }
  }

  async function handleUnpublish() {
    if (!agentId) return;
    try {
      await unpublish.mutateAsync();
      toast.success(t("publication.toasts.unpublished"));
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <div className="space-y-4">
      <PrePublishSummary draft={draft} agentId={agentId} />

      <div className="flex items-center gap-2">
        <input
          id="agent-requires-install-approval"
          type="checkbox"
          checked={draft.requires_install_approval}
          onChange={(e) =>
            onChange({
              ...draft,
              requires_install_approval: e.target.checked,
            })
          }
        />
        <Label htmlFor="agent-requires-install-approval">
          {t("publication.requiresInstallApproval")}
        </Label>
      </div>

      <div className="space-y-3 rounded-md border p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">
              {t("publication.stateTitle")}
            </h3>
            <p className="text-xs text-muted-foreground">
              {t("publication.publishHint")}
            </p>
          </div>
          {statusBadge(status)}
        </div>

        <div className="flex flex-wrap gap-2">
          {status === "draft" || status === undefined ? (
            <Button
              onClick={handleRequestPublish}
              disabled={!agentId || requestPublish.isPending}
            >
              {t("publication.requestPublish")}
            </Button>
          ) : null}
          {status === "published" ? (
            <Button
              variant="outline"
              onClick={handleUnpublish}
              disabled={unpublish.isPending}
            >
              {t("publication.unpublishArchive")}
            </Button>
          ) : null}
          {status === "pending_approval" ? (
            <p className="text-sm text-muted-foreground">
              {t("publication.waitingApprover")}
            </p>
          ) : null}
        </div>

        {requests.data && requests.data.length > 0 ? (
          <div className="mt-3 space-y-1">
            <h4 className="text-xs font-semibold uppercase text-muted-foreground">
              {t("publication.requestsHistoryTitle")}
            </h4>
            <ul className="space-y-1 text-xs">
              {requests.data.map((r) => (
                <li key={r.id} className="flex justify-between border-t pt-1">
                  <span>{new Date(r.created_at).toLocaleString()}</span>
                  <Badge variant="outline">{r.status}</Badge>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}
